/**
 * Sidebar Menu Module
 * Handles the sidebar navigation menu for desktop
 */

(function ($) {
	'use strict';

	/**
	 * Sidebar Menu Class
	 */
	class SidebarMenu {
		constructor() {
			this.sidebar = null;
			this.overlay = null;
			this.toggle = null;
			this.isOpen = false;

			// Bind methods
			this.init = this.init.bind(this);
			this.openMenu = this.openMenu.bind(this);
			this.closeMenu = this.closeMenu.bind(this);
			this.toggleMenu = this.toggleMenu.bind(this);
			this.handleKeyPress = this.handleKeyPress.bind(this);
			this.handleEscape = this.handleEscape.bind(this);
			this.trapFocus = this.trapFocus.bind(this);
			this.handleSubmenuToggle = this.handleSubmenuToggle.bind(this);

			this.init();
		}

		/**
		 * Initialize the sidebar menu
		 */
		init() {
			this.sidebar = document.getElementById('sidebar-menu-area');
			this.overlay = this.sidebar?.querySelector('.sidebar-menu-overlay');
			this.toggle = document.querySelector('.sidebar-menu-toggle');

			if (!this.sidebar || !this.toggle) {
				return;
			}

			this.bindEvents();
			this.setupAccessibility();
			this.setupSubmenuToggles();
		}

		/**
		 * Bind event listeners
		 */
		bindEvents() {
			// Toggle button
			this.toggle.addEventListener('click', this.toggleMenu);
			this.toggle.addEventListener('keydown', this.handleKeyPress);

			// Close button
			const closeBtn = this.sidebar.querySelector('.sidebar-menu-close');
			if (closeBtn) {
				closeBtn.addEventListener('click', this.closeMenu);
			}

			// Overlay click
			if (this.overlay) {
				this.overlay.addEventListener('click', this.closeMenu);
			}

			// Escape key
			document.addEventListener('keydown', this.handleEscape);
		}

		/**
		 * Setup submenu toggle functionality
		 */
		setupSubmenuToggles() {
			// Select ALL menu items with children at any nesting level
			const menuItemsWithChildren = this.sidebar.querySelectorAll(
				'.menu-item-has-children'
			);

			console.log(
				'Found menu items with children:',
				menuItemsWithChildren.length
			);

			menuItemsWithChildren.forEach((menuItem, index) => {
				// Get the direct child link only (not nested links)
				const link = menuItem.querySelector(':scope > a');
				if (!link) {
					console.warn('No link found for menu item', index);
					return;
				}

				// Check if already wrapped (prevent double-wrapping on re-initialization)
				if (
					link.parentElement &&
					link.parentElement.classList.contains('menu-item-wrapper')
				) {
					console.log('Already wrapped, skipping', index);
					return;
				}

				// Create wrapper div
				const wrapper = document.createElement('div');
				wrapper.className = 'menu-item-wrapper';

				// Create toggle button
				const toggleBtn = document.createElement('button');
				toggleBtn.className = 'submenu-toggle';
				toggleBtn.setAttribute('type', 'button');
				toggleBtn.setAttribute('aria-expanded', 'false');
				toggleBtn.setAttribute('aria-label', 'Toggle submenu');

				// Get parent before manipulating DOM
				const parent = link.parentNode;

				// Insert wrapper before the link
				parent.insertBefore(wrapper, link);

				// Move link into wrapper
				wrapper.appendChild(link);

				// Add toggle button to wrapper
				wrapper.appendChild(toggleBtn);

				// Add click event to toggle button only
				toggleBtn.addEventListener('click', this.handleSubmenuToggle);

				console.log('Wrapped menu item', index, menuItem);
			});
		}

		/**
		 * Handle submenu toggle
		 */
		handleSubmenuToggle(e) {
			e.preventDefault();
			e.stopPropagation();

			const toggleBtn = e.currentTarget;
			const parentItem = toggleBtn.closest('.menu-item-has-children');

			console.log('Toggle clicked', toggleBtn, parentItem);

			if (parentItem) {
				// Toggle active class
				const wasActive = parentItem.classList.contains('active');
				parentItem.classList.toggle('active');

				// Update aria-expanded
				const isExpanded = parentItem.classList.contains('active');
				toggleBtn.setAttribute('aria-expanded', isExpanded);

				console.log('Toggled from', wasActive, 'to', isExpanded);
			}
		}

		/**
		 * Setup accessibility attributes
		 */
		setupAccessibility() {
			this.sidebar.setAttribute('aria-hidden', 'true');
			this.toggle.setAttribute('aria-expanded', 'false');
			this.toggle.setAttribute('type', 'button');
		}

		/**
		 * Handle key press on toggle button
		 */
		handleKeyPress(e) {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				this.toggleMenu();
			}
		}

		/**
		 * Handle escape key press
		 */
		handleEscape(e) {
			if (e.key === 'Escape' && this.isOpen) {
				this.closeMenu();
			}
		}

		/**
		 * Open the sidebar menu
		 */
		openMenu() {
			if (this.isOpen) return;

			this.isOpen = true;

			// Update classes and attributes
			this.sidebar.classList.remove('sidebar-menu-hidden');
			this.sidebar.classList.add('sidebar-menu-visible');
			this.sidebar.setAttribute('aria-hidden', 'false');
			this.toggle.setAttribute('aria-expanded', 'true');

			// Prevent body scroll
			document.body.style.overflow = 'hidden';

			// Trap focus
			this.trapFocus();

			// Focus on close button
			setTimeout(() => {
				const closeBtn = this.sidebar.querySelector(
					'.sidebar-menu-close'
				);
				if (closeBtn) {
					closeBtn.focus();
				}
			}, 100);

			// Announce to screen readers
			this.announceToScreenReader(sidebarMenuData.i18n.openMenu);
		}

		/**
		 * Close the sidebar menu
		 */
		closeMenu() {
			if (!this.isOpen) return;

			this.isOpen = false;

			// Update classes and attributes
			this.sidebar.classList.remove('sidebar-menu-visible');
			this.sidebar.classList.add('sidebar-menu-hidden');
			this.sidebar.setAttribute('aria-hidden', 'true');
			this.toggle.setAttribute('aria-expanded', 'false');

			// Restore body scroll
			document.body.style.overflow = '';

			// Blur focus to remove focus effect
			if (this.toggle) {
				this.toggle.blur();
			}

			// Announce to screen readers
			this.announceToScreenReader(sidebarMenuData.i18n.closeMenu);
		}

		/**
		 * Toggle the sidebar menu
		 */
		toggleMenu() {
			if (this.isOpen) {
				this.closeMenu();
			} else {
				this.openMenu();
			}
		}

		/**
		 * Trap focus within the sidebar when open
		 */
		trapFocus() {
			if (!this.isOpen) return;

			const focusableElements = this.sidebar.querySelectorAll(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
			);

			const firstElement = focusableElements[0];
			const lastElement = focusableElements[focusableElements.length - 1];

			// Remove previous listener if exists
			this.sidebar.removeEventListener('keydown', this.currentFocusTrap);

			this.currentFocusTrap = (e) => {
				if (e.key === 'Tab') {
					if (e.shiftKey) {
						// Shift + Tab
						if (document.activeElement === firstElement) {
							e.preventDefault();
							lastElement.focus();
						}
					} else {
						// Tab
						if (document.activeElement === lastElement) {
							e.preventDefault();
							firstElement.focus();
						}
					}
				}
			};

			this.sidebar.addEventListener('keydown', this.currentFocusTrap);
		}

		/**
		 * Announce message to screen readers
		 */
		announceToScreenReader(message) {
			const announcement = document.createElement('div');
			announcement.setAttribute('aria-live', 'polite');
			announcement.setAttribute('aria-atomic', 'true');
			announcement.className = 'screen-reader-text';
			announcement.style.cssText = `
                position: absolute !important;
                width: 1px !important;
                height: 1px !important;
                padding: 0 !important;
                margin: -1px !important;
                overflow: hidden !important;
                clip: rect(0, 0, 0, 0) !important;
                white-space: nowrap !important;
                border: 0 !important;
            `;
			announcement.textContent = message;

			document.body.appendChild(announcement);

			setTimeout(() => {
				if (announcement.parentNode) {
					announcement.parentNode.removeChild(announcement);
				}
			}, 1000);
		}
	}

	/**
	 * Initialize on document ready
	 */
	$(document).ready(function () {
		// Check if sidebarMenuData is defined
		if (typeof sidebarMenuData !== 'undefined') {
			window.sidebarMenuInstance = new SidebarMenu();
		}
	});

	// Export for global access if needed
	window.SidebarMenu = SidebarMenu;
})(jQuery);
